import { useState, useEffect, useMemo } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import { useProjects } from "@/context/ProjectContext";
import { Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  FileText,
  Plus,
  Users,
  Paperclip,
  Clock,
  XCircle,
  CheckCircle,
  PauseCircle,
  Home,
  Edit2,
  Folder,
  ChevronDown,
  ChevronUp,
  Loader2,
  Key,
  Info,
  Building,
  CalendarDays,
  Activity,
} from "lucide-react";
import { format } from "date-fns";
import ProjectMembersTab from "@/components/projects/ProjectMembersTab";
import SubmissionDocumentsTab from "@/components/submissions/SubmissionDocumentsTab";
import ProjectTimelineTab from "@/components/projects/ProjectTimelineTab";
import ProjectCredentialsTab from "@/components/projects/ProjectCredentialsTab";
import { DraftSubmissionCard } from "@/components/submissions/DraftSubmissionCard";
import { Roles } from "@/enums/roles.enum";
import { projectApi } from "@/services/projectService";
import { ahjApi } from "@/services/ahjService";
import { IAHJ } from "@/types/ahj";
import { AHJFormDialog } from "@/components/ahj/AHJFormDialog";
import { AHJDetailsModal } from "@/components/ahj/AHJDetailsModal";
import { userApi } from "@/services/userService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { ProjectStatus } from "@/enums/project.enum";
import { Input } from "@/components/ui/input";
import MobileCard from "@/components/mobile/MobileCard";
import { useDeviceType } from "@/hooks/use-mobile";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { AHJRequirementsUpload } from "@/components/ahj/AHJRequirementsUpload";
import {
  GetProjectResponseDto,
  DocumentType,
  FileType,
  IAHJDocument,
} from "@/dto/get-project-response.dto";
import {
  calculateDaysUntilReview,
  getReviewDateBadgeVariant,
  getStatusBadgeVariant,
} from "@/utils/projectDetailHelpers";
import { CollapsibleCard } from "@/components/ui/CollapsibleCard";
import { MobileProjectInfo } from "@/components/projects/MobileProjectInfo";
import { MobileTabs } from "@/components/projects/MobileTabs";
import { ProjectValuationSection } from "./project-detail/components/sections/ProjectValuationSection";
import { SiteSafetySection } from "./project-detail/components/sections/SiteSafetySection";
import { CommodityDetailsSection } from "./project-detail/components/sections/CommodityDetailsSection";
import { StorageConfigurationSection } from "./project-detail/components/sections/StorageConfigurationSection";
import { HandlingOperationsSection } from "./project-detail/components/sections/HandlingOperationsSection";
import { SprinklerProtectionSection } from "./project-detail/components/sections/SprinklerProtectionSection";
import { EngineeringDocumentsSection } from "./project-detail/components/sections/EngineeringDocumentsSection";
import { cn } from "@/lib/utils";

const ProjectDetail = () => {
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getProjectById, updateStatus } = useProjects();
  const { user, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "timeline";

  const [activeTab, setActiveTab] = useState(initialTab);
  const queryClient = useQueryClient();
  const { isMobile } = useDeviceType();

  const project = getProjectById(id || "") as ReturnType<
    typeof getProjectById
  > & {
    permitNumber?: string;
    estimatedReviewDateByAHJ?: string | Date;
  };

  const {
    data: projectDetails,
    isFetching: isLoading,
    isError,
    status,
    refetch,
  } = useQuery({
    queryKey: ["projects", id],
    queryFn: () => projectApi.getProject(id),
    enabled: !!id && !!isAuthenticated,
    retry: 0,
  });

  // Fetch all users for project manager selection (Admin only)
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: userApi.getUsers,
    enabled: user?.role === Roles.ADMIN,
  });

  // Fetch all AHJs for AHJ selection
  const { data: ahjsData } = useQuery({
    queryKey: ["ahjs"],
    queryFn: () => ahjApi.getAHJs({}),
    enabled: !!isAuthenticated,
  });

  // Members can only view and comment, no actions
  const canPerformActions =
    projectDetails &&
    user?.role !== Roles.MEMBER &&
    (user?.role === Roles.ADMIN ||
      user?.role === Roles.CONTRACTOR ||
      user?.role === Roles.CLIENT ||
      user?._id === projectDetails.createdBy._id);

  // Show error toast when query fails
  useEffect(() => {
    if (status === "error") {
      toast({
        title: "Unable to fetch project details",
        variant: "destructive",
      });
    }
  }, [status, toast]);

  const [pendingStatus, setPendingStatus] = useState<ProjectStatus | null>(
    null
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Inline edit state for new fields
  const [editingField, setEditingField] = useState<
    | "permitNumber"
    | "estimatedReviewDateByAHJ"
    | "projectManager"
    | "ahj"
    | null
  >(null);
  const [tempPermitNumber, setTempPermitNumber] = useState("");
  const [tempEstimatedReviewDate, setTempEstimatedReviewDate] = useState(""); // yyyy-MM-dd
  const [tempProjectManager, setTempProjectManager] = useState(""); // User ID
  const [tempAHJ, setTempAHJ] = useState(""); // AHJ ID
  const [updatingField, setUpdatingField] = useState<
    | "permitNumber"
    | "estimatedReviewDateByAHJ"
    | "projectManager"
    | "ahj"
    | null
  >(null);
  const [ahjDialogOpen, setAhjDialogOpen] = useState(false);
  const [ahjDetailsOpen, setAhjDetailsOpen] = useState(false);
  const [ahjUploadOpen, setAhjUploadOpen] = useState(false);

  const missingAHJRequirements = useMemo(() => {
    if (
      !projectDetails?.ahj ||
      typeof projectDetails.ahj === "string" ||
      !projectDetails.ahj.permittingRequirements
    )
      return [];

    const requiredKeys = Object.entries(
      projectDetails.ahj.permittingRequirements
    )
      .filter(([_, required]) => required)
      .map(([key]) => key);

    const uploadedKeys = (projectDetails.ahjDocuments || []).map(
      (d: IAHJDocument) => d.requirementKey
    );

    return requiredKeys.filter((key) => !uploadedKeys.includes(key));
  }, [projectDetails]);

  useEffect(() => {
    if (projectDetails) {
      setTempPermitNumber(projectDetails.permitNumber || "");
      setTempEstimatedReviewDate(
        projectDetails.estimatedReviewDateByAHJ
          ? new Date(projectDetails.estimatedReviewDateByAHJ)
              .toISOString()
              .substring(0, 10)
          : ""
      );
      setTempProjectManager(projectDetails.projectManager?._id || "");
      setTempAHJ(projectDetails.ahj?._id || "");
    }
  }, [projectDetails]);

  const { mutate: updateProject, isPending: updating } = useMutation({
    mutationFn: (
      data: Partial<{
        permitNumber: string;
        estimatedReviewDateByAHJ: string;
        projectManager: string;
        ahj: string;
      }>
    ) => projectApi.updateProject(projectDetails?._id || "", data),
    onMutate: () => {
      // Set which field is being updated based on editingField
      setUpdatingField(editingField);
    },
    onSuccess: () => {
      toast({ title: "Project updated" });
      setEditingField(null);
      setUpdatingField(null);
      // refresh cached data
      queryClient.invalidateQueries({ queryKey: ["projects", id] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      refetch();
    },
    onError: () => {
      toast({ title: "Update failed", variant: "destructive" });
      setUpdatingField(null);
    },
  });

  const handleAHJCreated = (ahj: IAHJ) => {
    setTempAHJ(ahj._id);
    setAhjDialogOpen(false);
  };

  const handleAHJEdited = (ahj: IAHJ) => {
    // Refresh project data after AHJ is edited
    queryClient.invalidateQueries({ queryKey: ["projects", id] });
    queryClient.invalidateQueries({ queryKey: ["projects"] });
    queryClient.invalidateQueries({ queryKey: ["ahjs"] });
    refetch();
  };

  const handleSaveField = () => {
    if (!editingField) return;
    if (editingField === "permitNumber") {
      updateProject({ permitNumber: tempPermitNumber || "" });
    } else if (editingField === "estimatedReviewDateByAHJ") {
      updateProject({
        estimatedReviewDateByAHJ: tempEstimatedReviewDate
          ? new Date(tempEstimatedReviewDate).toISOString()
          : "",
      });
    } else if (editingField === "projectManager") {
      updateProject({ projectManager: tempProjectManager || "" });
    } else if (editingField === "ahj") {
      updateProject({ ahj: tempAHJ || "" });
    }
  };

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <h2 className="text-2xl font-semibold mb-4">Project not found</h2>
        <Button onClick={() => navigate("/projects")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Projects
        </Button>
      </div>
    );
  }

  const getStatusBadgeVariant = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.PENDING:
        return "default";
      case ProjectStatus.ON_HOLD:
        return "warning";
      case ProjectStatus.REJECTED:
        return "destructive";
      case ProjectStatus.COMPLETED:
        return "success";
      default:
        return "outline";
    }
  };

  const triggerStatusChange = (newStatus: ProjectStatus) => {
    setPendingStatus(newStatus);
    setConfirmOpen(true);
  };

  const handleStatusConfirm = () => {
    if (pendingStatus) {
      updateStatus(projectDetails._id, pendingStatus);
    }
    setConfirmOpen(false);
    setPendingStatus(null);
  };

  const canChangeTo = (targetStatus: ProjectStatus): boolean => {
    if (user?.role !== Roles.ADMIN) {
      return false;
    }
    return targetStatus !== project.status;
  };

  const renderStatusActions = () => {
    const buttons = [
      {
        label: "Mark Pending",
        status: ProjectStatus.PENDING,
        icon: Clock,
      },
      {
        label: "Mark Submitted",
        status: ProjectStatus.SUBMITTED,
        icon: Folder,
      },
      {
        label: "Mark In Progress",
        status: ProjectStatus.IN_PROGRESS,
        icon: Folder,
      },
      {
        label: "Mark On Hold",
        status: ProjectStatus.ON_HOLD,
        icon: PauseCircle,
      },
      {
        label: "Mark Completed",
        status: ProjectStatus.COMPLETED,
        icon: CheckCircle,
      },
      {
        label: "Reject",
        status: ProjectStatus.REJECTED,
        icon: XCircle,
      },
    ];

    if (buttons.every((b) => !canChangeTo(b.status))) return;

    return (
      <div className="mt-4 w-full max-w-xs">
        <Select
          onValueChange={(value) => triggerStatusChange(value as ProjectStatus)}
        >
          <SelectTrigger className="w-full h-10 text-sm shadow-sm border-input/60 bg-background/50 backdrop-blur-sm hover:bg-accent/50 transition-colors">
            <SelectValue placeholder="Change status" />
          </SelectTrigger>
          <SelectContent className="shadow-xl border-border/60 bg-white dark:bg-popover">
            {buttons.map(({ label, status, icon: Icon }) => {
              const disabled = !canChangeTo(status);
              return (
                <SelectItem
                  key={status}
                  value={status}
                  disabled={disabled}
                  className="cursor-pointer focus:bg-accent focus:text-accent-foreground data-[disabled]:opacity-70"
                >
                  <div className="flex items-center gap-2.5 py-0.5">
                    <div
                      className={cn(
                        "p-1 rounded-full bg-muted/50",
                        status === ProjectStatus.COMPLETED &&
                          "text-green-600 bg-green-100/50",
                        status === ProjectStatus.REJECTED &&
                          "text-red-600 bg-red-100/50",
                        status === ProjectStatus.ON_HOLD &&
                          "text-amber-600 bg-amber-100/50",
                        status === ProjectStatus.SUBMITTED &&
                          "text-blue-600 bg-blue-100/50"
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <span
                      className={cn(
                        "text-sm font-medium",
                        disabled ? "text-muted-foreground" : "text-foreground"
                      )}
                    >
                      {label}
                    </span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    );
  };

  const confirmDialog = (
    <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Status Change</DialogTitle>
          <DialogDescription>
            {pendingStatus ? (
              <span>
                Are you sure you want to change the project status to{" "}
                <strong>{pendingStatus.replace(/_/g, " ")}</strong>? This action
                will be visible to all members.
              </span>
            ) : (
              "Select a status to continue."
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => {
              setConfirmOpen(false);
              setPendingStatus(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleStatusConfirm} disabled={!pendingStatus}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const canCreateSubmission =
    projectDetails &&
    user?.role !== Roles.MEMBER &&
    (user?.role === Roles.ADMIN ||
      user?.role === Roles.CONTRACTOR ||
      user?.role === Roles.CLIENT ||
      user?._id === projectDetails.createdBy._id);

  const submission =
    !isLoading && projectDetails?.submission ? projectDetails.submission : null;

  const missingDocumentsAlert =
    missingAHJRequirements.length > 0 ? (
      <Alert
        variant="destructive"
        className="mb-6 flex items-center justify-between py-5 px-6 shadow-lg border-l-4 border-l-destructive bg-card rounded-xl ring-1 ring-black/5"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 bg-destructive/10 rounded-full shadow-inner">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-bold text-lg text-foreground">
              Missing Required AHJ Documents
            </span>
            <span className="text-muted-foreground">
              You are missing{" "}
              <span className="font-semibold text-destructive">
                {missingAHJRequirements.length}
              </span>{" "}
              required document(s) for the AHJ.
            </span>
          </div>
        </div>
        <Button
          variant="default"
          size="lg"
          onClick={() => setAhjUploadOpen(true)}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md hover:shadow-lg transition-all rounded-lg font-semibold px-6"
        >
          Upload Now
        </Button>
      </Alert>
    ) : null;

  return (
    <div className="min-h-screen bg-background/50 pb-20">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <span className="text-muted-foreground text-lg">
            Loading project details...
          </span>
        </div>
      ) : (
        <>
          {/* Header Section with Gradient Background */}
          <div className="relative bg-card border-b shadow-sm">
            <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent pointer-events-none" />
            <div className="container mx-auto px-4 py-6 relative z-10">
              <div className="flex flex-col gap-6">
                {/* Breadcrumb & Back Navigation */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 -ml-2 hover:text-foreground transition-colors"
                    onClick={() => navigate("/projects")}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Projects
                  </Button>
                  <span>/</span>
                  <span className="font-medium text-foreground truncate max-w-[200px]">
                    {project?.name || "Loading..."}
                  </span>
                </div>

                {/* Project Title & Actions */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                        {project?.name}
                      </h1>
                      {project && (
                        <Badge
                          variant={getStatusBadgeVariant(project.status)}
                          className="h-6 px-2 text-xs uppercase tracking-wide shadow-sm"
                        >
                          {project.status.replace("_", " ")}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Building className="h-4 w-4" />
                        <span>{project?.ahj?.name || "No AHJ Assigned"}</span>
                      </div>
                      <div className="w-1 h-1 rounded-full bg-border" />
                      <div className="flex items-center gap-1.5">
                        <CalendarDays className="h-4 w-4" />
                        <span>
                          Created{" "}
                          {project &&
                            format(new Date(project.createdAt), "MMM d, yyyy")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 w-full md:w-auto">
                    {/* Action Buttons */}
                    {renderStatusActions()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Main Content Area */}
              <div className="lg:col-span-8 space-y-6">
                {/* Tabs Navigation */}
                <Tabs
                  value={activeTab}
                  onValueChange={(value) => {
                    setActiveTab(value);
                    setSearchParams({ tab: value });
                  }}
                  className="w-full"
                >
                  <div className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-4 -mx-4 px-4 md:mx-0 md:px-0">
                    <TabsList className="w-full justify-start h-16 p-2 bg-slate-100/80 dark:bg-muted/40 rounded-2xl border shadow-inner gap-2 overflow-x-auto flex-nowrap no-scrollbar">
                      <TabsTrigger
                        value="timeline"
                        className="flex-none px-4 h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground whitespace-nowrap"
                      >
                        <Activity className="h-4 w-4 mr-2.5" />
                        Timeline
                      </TabsTrigger>
                      <TabsTrigger
                        value="submissions"
                        className="flex-none px-4 h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground whitespace-nowrap"
                      >
                        <FileText className="h-4 w-4 mr-2.5" />
                        Submission
                      </TabsTrigger>
                      <TabsTrigger
                        value="documents"
                        className="flex-none px-4 h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground whitespace-nowrap"
                      >
                        <Paperclip className="h-4 w-4 mr-2.5" />
                        Documents
                      </TabsTrigger>
                      <TabsTrigger
                        value="members"
                        className="flex-none px-4 h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground whitespace-nowrap"
                      >
                        <Users className="h-4 w-4 mr-2.5" />
                        Team
                      </TabsTrigger>
                      {user?.role === Roles.ADMIN && (
                        <TabsTrigger
                          value="credentials"
                          className="flex-none px-4 h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground whitespace-nowrap"
                        >
                          <Key className="h-4 w-4 mr-2.5" />
                          Credentials
                        </TabsTrigger>
                      )}
                    </TabsList>
                  </div>

                  <TabsContent
                    value="timeline"
                    className="mt-0 space-y-6 animate-in fade-in-50 slide-in-from-bottom-2 duration-500"
                  >
                    <ProjectTimelineTab
                      project={
                        projectDetails ||
                        (project as unknown as GetProjectResponseDto)
                      }
                      submission={submission}
                      statusHistory={projectDetails?.statusHistory || []}
                    />
                  </TabsContent>

                  <TabsContent
                    value="submissions"
                    className="mt-0 space-y-6 animate-in fade-in-50 slide-in-from-bottom-2 duration-500"
                  >
                    {submission ? (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h2 className="text-xl font-semibold">Submission</h2>
                          {canPerformActions && (
                            <Button
                              size="sm"
                              onClick={() =>
                                navigate(
                                  `/projects/${projectDetails?._id}/submissions/edit`
                                )
                              }
                            >
                              <Edit2 className="mr-2 h-4 w-4" />
                              Edit
                            </Button>
                          )}
                        </div>
                        <ProjectValuationSection submission={submission} />
                        <SiteSafetySection submission={submission} />
                        <CommodityDetailsSection
                          submission={submission}
                          canCreateSubmission={canCreateSubmission}
                          projectDetails={projectDetails}
                          navigate={navigate}
                        />
                        <StorageConfigurationSection submission={submission} />
                        <HandlingOperationsSection submission={submission} />
                        <SprinklerProtectionSection submission={submission} />
                        <EngineeringDocumentsSection submission={submission} />
                      </div>
                    ) : (
                      <Card className="flex flex-col items-center justify-center py-16 border-dashed border-2 shadow-sm bg-white dark:bg-card">
                        <div className="bg-muted/30 p-4 rounded-full shadow-sm mb-4 ring-1 ring-border/50">
                          <FileText className="h-10 w-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-xl font-semibold mb-2">
                          No submission yet
                        </h3>
                        <p className="text-muted-foreground mb-6 text-center max-w-sm">
                          Create a submission to add project details.
                        </p>
                        {canCreateSubmission && (
                          <Button
                            onClick={() =>
                              navigate(
                                `/projects/${project._id}/submissions/new`
                              )
                            }
                            className="shadow-md hover:shadow-lg transition-all"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Submission
                          </Button>
                        )}
                      </Card>
                    )}
                  </TabsContent>

                  <TabsContent
                    value="documents"
                    className="mt-0 space-y-6 animate-in fade-in-50 slide-in-from-bottom-2 duration-500"
                  >
                    {submission ||
                    (projectDetails?.ahjDocuments &&
                      projectDetails.ahjDocuments.length > 0) ? (
                      <SubmissionDocumentsTab
                        projectId={projectDetails?._id || project._id}
                        submission={submission}
                        project={
                          projectDetails ||
                          (project as unknown as GetProjectResponseDto)
                        }
                        onRefresh={refetch}
                      />
                    ) : (
                      <Card className="flex flex-col items-center justify-center py-16 border-dashed border-2 shadow-sm bg-white dark:bg-card">
                        <div className="bg-muted/30 p-4 rounded-full shadow-sm mb-4 ring-1 ring-border/50">
                          <Paperclip className="h-10 w-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-xl font-semibold mb-2">
                          No documents yet
                        </h3>
                        <p className="text-muted-foreground mb-6 text-center max-w-sm">
                          Create a submission first to add documents.
                        </p>
                        {canCreateSubmission && (
                          <Button
                            onClick={() =>
                              navigate(
                                `/projects/${project._id}/submissions/new`
                              )
                            }
                            className="shadow-md hover:shadow-lg transition-all"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Submission
                          </Button>
                        )}
                      </Card>
                    )}
                  </TabsContent>

                  <TabsContent
                    value="members"
                    className="mt-0 space-y-6 animate-in fade-in-50 slide-in-from-bottom-2 duration-500"
                  >
                    <ProjectMembersTab
                      project={
                        projectDetails ||
                        (project as unknown as GetProjectResponseDto)
                      }
                    />
                  </TabsContent>

                  {user?.role === Roles.ADMIN && (
                    <TabsContent
                      value="credentials"
                      className="mt-0 space-y-6 animate-in fade-in-50 slide-in-from-bottom-2 duration-500"
                    >
                      <ProjectCredentialsTab
                        projectId={projectDetails?._id || project._id}
                      />
                    </TabsContent>
                  )}
                </Tabs>
              </div>

              {/* Sidebar Info */}
              <div className="lg:col-span-4 space-y-6">
                {/* Project Info Card */}
                <CollapsibleCard
                  title="Project Information"
                  defaultOpen={true}
                  action={
                    canPerformActions ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 rounded-full hover:bg-muted"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${id}/edit`);
                        }}
                        title="Edit Project"
                      >
                        <Edit2 className="h-4 w-4 text-muted-foreground hover:text-primary" />
                      </Button>
                    ) : null
                  }
                >
                  <MobileProjectInfo
                    project={project}
                    projectDetails={projectDetails}
                    user={user}
                    editingField={editingField}
                    setEditingField={setEditingField}
                    tempPermitNumber={tempPermitNumber}
                    setTempPermitNumber={setTempPermitNumber}
                    tempEstimatedReviewDate={tempEstimatedReviewDate}
                    setTempEstimatedReviewDate={setTempEstimatedReviewDate}
                    tempProjectManager={tempProjectManager}
                    setTempProjectManager={setTempProjectManager}
                    tempAHJ={tempAHJ}
                    setTempAHJ={setTempAHJ}
                    updatingField={updatingField}
                    handleSaveField={handleSaveField}
                    canPerformActions={canPerformActions}
                    usersData={usersData}
                    ahjsData={ahjsData}
                  />
                </CollapsibleCard>

                {missingDocumentsAlert}
              </div>
            </div>
          </div>

          {/* Modals */}
          {confirmDialog}

          <AHJDetailsModal
            ahj={projectDetails?.ahj}
            open={ahjDetailsOpen}
            onOpenChange={setAhjDetailsOpen}
            showEditButton={user?.role === Roles.ADMIN}
            onEdit={handleAHJEdited}
            projectDocuments={projectDetails?.ahjDocuments}
            onUpload={() => setAhjUploadOpen(true)}
          />

          <AHJFormDialog
            open={ahjDialogOpen}
            onOpenChange={setAhjDialogOpen}
            onSuccess={handleAHJCreated}
          />

          <Dialog open={ahjUploadOpen} onOpenChange={setAhjUploadOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              {projectDetails?.ahj && (
                <AHJRequirementsUpload
                  projectId={projectDetails._id}
                  ahjId={
                    typeof projectDetails.ahj === "string"
                      ? projectDetails.ahj
                      : projectDetails.ahj._id
                  }
                  onComplete={() => {
                    setAhjUploadOpen(false);
                    refetch();
                  }}
                  onSkip={() => setAhjUploadOpen(false)}
                  showContinueButton={false}
                />
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default ProjectDetail;
